import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { contentService } from '../../../services/contentService';
import { useAuth } from '../../../contexts/AuthContext';

const ContentManagementPanel = () => {
  const [activeTab, setActiveTab] = useState('pages');
  const [searchQuery, setSearchQuery] = useState('');
  const [contentItems, setContentItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingContent, setEditingContent] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    type: 'page',
    status: 'draft',
    body: ''
  });
  const { userProfile } = useAuth();

  const convertToDisplayFormat = (item) => ({
    id: item?.id,
    title: item?.title,
    type: item?.type,
    status: item?.status,
    lastModified: new Date(item.updatedAt)?.toLocaleString('fr-FR', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    }),
    author: userProfile?.full_name || 'Unknown'
  });

  useEffect(() => {
    loadContent();
    
    // Subscribe to real-time changes
    const unsubscribe = contentService?.subscribeToChanges((payload) => {
      // payload.new from Supabase is in snake_case, convert before display
      const convertPayload = (raw) => ({
        id: raw?.id,
        title: raw?.title,
        type: raw?.content_type,
        status: raw?.status,
        updatedAt: raw?.updated_at,
      });
      if (payload?.eventType === 'INSERT') {
        setContentItems(prev => [convertToDisplayFormat(convertPayload(payload?.new)), ...prev]);
      } else if (payload?.eventType === 'UPDATE') {
        setContentItems(prev => prev?.map(item =>
          item?.id === payload?.new?.id ? convertToDisplayFormat(convertPayload(payload?.new)) : item
        ));
      } else if (payload?.eventType === 'DELETE') {
        setContentItems(prev => prev?.filter(item => item?.id !== payload?.old?.id));
      }
    });

    return () => unsubscribe();
  }, []);

  const loadContent = async () => {
    try {
      setLoading(true);
      const data = await contentService?.getAll();
      setContentItems(data?.map(convertToDisplayFormat));
    } catch (err) {
      setError(err?.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this content?')) return;
    
    try {
      await contentService?.delete(id);
    } catch (err) {
      alert('Error deleting content: ' + err?.message);
    }
  };

  const tabs = [
    { id: 'pages', label: 'Pages', icon: 'FileText' },
    { id: 'blog', label: 'Blog Posts', icon: 'BookOpen' },
    { id: 'announcements', label: 'Announcements', icon: 'Megaphone' },
    { id: 'media', label: 'Media Library', icon: 'Image' }
  ];

  const statusOptions = [
    { value: 'all', label: 'All Status' },
    { value: 'published', label: 'Published' },
    { value: 'draft', label: 'Draft' },
    { value: 'scheduled', label: 'Scheduled' }
  ];

  const getStatusColor = (status) => {
    const colors = {
      published: 'var(--color-success)',
      draft: 'var(--color-warning)',
      scheduled: 'var(--color-trust)'
    };
    return colors?.[status] || 'var(--color-muted-foreground)';
  };

  const handleOpenModal = (content = null) => {
    if (content) {
      setEditingContent(content);
      setFormData({
        title: content?.title || '',
        type: content?.type || 'page',
        status: content?.status || 'draft',
        body: content?.body || ''
      });
    } else {
      setEditingContent(null);
      setFormData({
        title: '',
        type: activeTab === 'pages' ? 'page' : activeTab === 'blog' ? 'blog' : 'announcement',
        status: 'draft',
        body: ''
      });
    }
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingContent(null);
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();

    try {
      if (editingContent) {
        await contentService?.update(editingContent?.id, formData);
      } else {
        await contentService?.create(formData);
      }
      handleCloseModal();
      loadContent();
    } catch (err) {
      setError(err?.message);
    }
  };

  if (loading) {
    return (
      <div className="bg-card rounded-xl shadow-premium border border-border p-8 text-center">
        <p className="text-muted-foreground">Loading content...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-card rounded-xl shadow-premium border border-border p-8 text-center">
        <p className="text-error">Error: {error}</p>
      </div>
    );
  }

  return (
    <>
      <div className="bg-card rounded-xl shadow-premium border border-border">
        <div className="p-4 md:p-6 border-b border-border">
          <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
            <h3 className="text-lg md:text-xl font-bold text-foreground">Content Management</h3>
            <Button 
              variant="default" 
              iconName="Plus" 
              iconPosition="left" 
              size="sm"
              onClick={() => handleOpenModal()}
            >
              Create New Content
            </Button>
          </div>

          <div className="flex items-center gap-2 mt-4 overflow-x-auto pb-2">
            {tabs?.map((tab) => (
              <button
                key={tab?.id}
                onClick={() => setActiveTab(tab?.id)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all whitespace-nowrap flex-shrink-0 ${
                  activeTab === tab?.id
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-muted text-muted-foreground hover:bg-muted/80'
                }`}
              >
                <Icon name={tab?.icon} size={16} />
                {tab?.label}
              </button>
            ))}
          </div>
        </div>
        <div className="p-4 md:p-6">
          <div className="flex flex-col md:flex-row gap-4 mb-6">
            <div className="flex-1">
              <Input
                type="search"
                placeholder="Search content..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e?.target?.value)}
              />
            </div>
            <Select
              options={statusOptions}
              value="all"
              onChange={() => {}}
              className="w-40"
            />
          </div>

          <div className="space-y-3">
            {contentItems?.map((item) => (
              <div
                key={item?.id}
                className="bg-muted/50 rounded-xl p-4 hover:bg-muted transition-colors"
              >
                <div className="flex items-center justify-between">
                  <div className="flex-1 min-w-0">
                    <h4 className="font-semibold text-foreground mb-1">{item?.title}</h4>
                    <div className="flex items-center gap-3 text-xs text-muted-foreground">
                      <span className="flex items-center gap-1">
                        <Icon name="User" size={12} />
                        {item?.author}
                      </span>
                      <span>•</span>
                      <span className="flex items-center gap-1">
                        <Icon name="Clock" size={12} />
                        {item?.lastModified}
                      </span>
                      <span>•</span>
                      <span
                        className="px-2 py-1 rounded text-xs font-medium"
                        style={{
                          backgroundColor: `${getStatusColor(item?.status)}15`,
                          color: getStatusColor(item?.status),
                        }}
                      >
                        {item?.status?.toUpperCase()}
                      </span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <button 
                      onClick={() => handleOpenModal(item)}
                      className="w-8 h-8 rounded-lg hover:bg-background flex items-center justify-center transition-colors"
                    >
                      <Icon name="Edit" size={16} className="text-muted-foreground" />
                    </button>
                    <button
                      onClick={() => handleDelete(item?.id)}
                      className="w-8 h-8 rounded-lg hover:bg-background flex items-center justify-center transition-colors"
                    >
                      <Icon name="Trash2" size={16} className="text-error" />
                    </button>
                  </div>
                </div>
              </div>
            ))}

            {contentItems?.length === 0 && (
              <div className="text-center py-12">
                <Icon name="FileText" size={48} className="text-muted-foreground mx-auto mb-4" />
                <p className="text-muted-foreground">No content found</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Create/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-card rounded-xl shadow-premium max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-border">
              <div className="flex items-center justify-between">
                <h3 className="text-xl font-bold text-foreground">
                  {editingContent ? 'Edit Content' : 'Create New Content'}
                </h3>
                <button
                  onClick={handleCloseModal}
                  className="w-8 h-8 rounded-lg hover:bg-muted flex items-center justify-center transition-colors"
                >
                  <Icon name="X" size={20} />
                </button>
              </div>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <Input
                label="Title *"
                value={formData?.title}
                onChange={(e) => setFormData({ ...formData, title: e?.target?.value })}
                required
              />

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">Type *</label>
                  <Select
                    options={[
                      { value: 'page', label: 'Page' },
                      { value: 'blog', label: 'Blog Post' },
                      { value: 'announcement', label: 'Announcement' }
                    ]}
                    value={formData?.type}
                    onChange={(value) => setFormData({ ...formData, type: value })}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">Status *</label>
                  <Select
                    options={statusOptions?.filter(opt => opt?.value !== 'all')}
                    value={formData?.status}
                    onChange={(value) => setFormData({ ...formData, status: value })}
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground mb-2">Content *</label>
                <textarea
                  value={formData?.body}
                  onChange={(e) => setFormData({ ...formData, body: e?.target?.value })}
                  rows={10}
                  required
                  className="w-full px-4 py-2 bg-background border border-border rounded-lg text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                />
              </div>

              <div className="flex items-center gap-3 pt-4">
                <Button type="submit" variant="default" className="flex-1">
                  {editingContent ? 'Update' : 'Create'}
                </Button>
                <Button type="button" variant="outline" onClick={handleCloseModal} className="flex-1">
                  Cancel
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
};

export default ContentManagementPanel;